/**
 * generateCisReport.ts - v2026.06.172/173
 * Génère des rapports PDF + Word filtrés par référentiel CIS (CIS-M365 ou CIS-Azure).
 *
 * Structure :
 *   - Page de garde (logo bouclier + titre rapport + nom organisation + date)
 *   - 1. Synthèse globale (count total / conformes / non-conformes / non-évalués + score)
 *   - 2. Tableau complet : ID NIS365, Titre, Réf. CIS, Statut, Preuve (message dernier scan)
 *
 * Utilisation :
 *   downloadCisReportPdf('CIS-M365', controls, scanByControl, meta)
 *   downloadCisReportDocx('CIS-Azure', azureControls, scanByControl, meta)
 *
 * La langue est passée via meta.lang ('fr-BE'|'nl-BE'|'de-BE'|'en') ; les titres et
 * libellés sont localisés. Les références CIS (ex: "Acct1.1.1 – Ensure...") restent en EN
 * conformément à la spec officielle CIS.
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';
import {
  Document, Packer, Paragraph, Table, TableRow, TableCell, TextRun,
  WidthType, AlignmentType,
} from 'docx';
import type { ControlReference } from '../domain/types';

// =============================================================================
// Types
// =============================================================================

export type CisFramework = 'CIS-M365' | 'CIS-Azure';
export type ReportLang   = 'fr-BE' | 'nl-BE' | 'de-BE' | 'en';

export interface CisReportMeta {
  orgName:     string;
  date:        string;
  lang?:       ReportLang;
  company?:    {
    legalName?:  string;
    bce?:        string;
    address?:    string;
    postalCode?: string;
    city?:       string;
    country?:    string;
    contact?:    string;
  };
}

export interface CisScanHit { status?: string; message?: string; date?: string; }

// Union supportée : M365 controls + Azure controls (les 2 ont id/title/references)
type AnyControl = {
  id: string;
  title: string;
  references: ControlReference[];
};

// =============================================================================
// i18n strings (titres + headers + libellés)
// =============================================================================

interface I18n {
  reportTitle:    (fw: CisFramework) => string;
  generatedOn:    string;
  syntheseTitle:  string;
  total:          string;
  conformes:      string;
  nonConformes:   string;
  ameliorer:      string;
  nonEvalues:     string;
  score:          string;
  tableTitle:     (fw: CisFramework) => string;
  colId:          string;
  colTitle:       string;
  colRef:         string;
  colStatus:      string;
  colEvidence:    string;
  statusPass:     string;
  statusFail:     string;
  statusWarning:  string;
  statusNa:       string;
  noEvidence:     string;
  noScan:         string;
  confidential:   string;
}

const STRINGS: Record<ReportLang, I18n> = {
  'fr-BE': {
    reportTitle: (fw) => `Rapport ${fw}`,
    generatedOn: 'Généré le',
    syntheseTitle: '1. Synthèse globale',
    total: 'Total contrôles', conformes: 'Conformes', nonConformes: 'Non conformes',
    ameliorer: 'À améliorer', nonEvalues: 'Non évalués', score: 'Score de conformité',
    tableTitle: (fw) => `2. Détail des contrôles ${fw}`,
    colId: 'ID', colTitle: 'Contrôle', colRef: 'Réf. CIS', colStatus: 'Statut', colEvidence: 'Preuve / message',
    statusPass: 'Conforme', statusFail: 'Non conforme', statusWarning: 'À améliorer', statusNa: 'Non évalué',
    noEvidence: '—', noScan: 'Pas de scan récent',
    confidential: 'Document confidentiel — généré par NIS365 Auditor',
  },
  'nl-BE': {
    reportTitle: (fw) => `${fw}-rapport`,
    generatedOn: 'Gegenereerd op',
    syntheseTitle: '1. Globale samenvatting',
    total: 'Totaal controles', conformes: 'Conform', nonConformes: 'Niet conform',
    ameliorer: 'Te verbeteren', nonEvalues: 'Niet geëvalueerd', score: 'Conformiteitsscore',
    tableTitle: (fw) => `2. Detail ${fw}-controles`,
    colId: 'ID', colTitle: 'Controle', colRef: 'CIS-ref.', colStatus: 'Status', colEvidence: 'Bewijs / bericht',
    statusPass: 'Conform', statusFail: 'Niet conform', statusWarning: 'Te verbeteren', statusNa: 'Niet geëvalueerd',
    noEvidence: '—', noScan: 'Geen recente scan',
    confidential: 'Vertrouwelijk document — gegenereerd door NIS365 Auditor',
  },
  'de-BE': {
    reportTitle: (fw) => `${fw}-Bericht`,
    generatedOn: 'Erstellt am',
    syntheseTitle: '1. Globale Zusammenfassung',
    total: 'Gesamt Prüfungen', conformes: 'Konform', nonConformes: 'Nicht konform',
    ameliorer: 'Zu verbessern', nonEvalues: 'Nicht ausgewertet', score: 'Konformitätsscore',
    tableTitle: (fw) => `2. Detail ${fw}-Prüfungen`,
    colId: 'ID', colTitle: 'Prüfung', colRef: 'CIS-Ref.', colStatus: 'Status', colEvidence: 'Nachweis / Nachricht',
    statusPass: 'Konform', statusFail: 'Nicht konform', statusWarning: 'Zu verbessern', statusNa: 'Nicht ausgewertet',
    noEvidence: '—', noScan: 'Kein aktueller Scan',
    confidential: 'Vertrauliches Dokument — erstellt von NIS365 Auditor',
  },
  'en': {
    reportTitle: (fw) => `${fw} Report`,
    generatedOn: 'Generated on',
    syntheseTitle: '1. Global summary',
    total: 'Total controls', conformes: 'Compliant', nonConformes: 'Non compliant',
    ameliorer: 'To improve', nonEvalues: 'Not evaluated', score: 'Compliance score',
    tableTitle: (fw) => `2. ${fw} controls detail`,
    colId: 'ID', colTitle: 'Control', colRef: 'CIS Ref.', colStatus: 'Status', colEvidence: 'Evidence / message',
    statusPass: 'Compliant', statusFail: 'Non compliant', statusWarning: 'To improve', statusNa: 'Not evaluated',
    noEvidence: '—', noScan: 'No recent scan',
    confidential: 'Confidential document — generated by NIS365 Auditor',
  },
};

// =============================================================================
// Helpers
// =============================================================================

const BRAND: [number, number, number] = [31, 61, 92];
const ACCENT: [number, number, number] = [45, 139, 150];
const GREEN: [number, number, number] = [31, 122, 69];
const RED: [number, number, number]   = [184, 35, 41];
const ORANGE: [number, number, number] = [168, 99, 23];
const GREY: [number, number, number]  = [120, 120, 120];

function today(): string { return new Date().toISOString().slice(0, 10); }
function safeName(s: string): string { return (s || 'organisation').replace(/[^\w]+/g, '_'); }
function companyName(meta: CisReportMeta): string {
  return meta.company?.legalName || meta.orgName || '—';
}

function statusKey(s?: string): 'pass' | 'fail' | 'warning' | 'na' {
  const v = (s ?? '').toLowerCase();
  if (v === 'pass') return 'pass';
  if (v === 'fail' || v === 'error') return 'fail';
  if (v === 'warning' || v === 'warn') return 'warning';
  return 'na';
}

function statusLabel(s: 'pass' | 'fail' | 'warning' | 'na', i: I18n): string {
  return s === 'pass' ? i.statusPass : s === 'fail' ? i.statusFail : s === 'warning' ? i.statusWarning : i.statusNa;
}

function statusColor(s: 'pass' | 'fail' | 'warning' | 'na'): [number, number, number] {
  return s === 'pass' ? GREEN : s === 'fail' ? RED : s === 'warning' ? ORANGE : GREY;
}

interface CisRow {
  id:       string;
  title:    string;
  cisRef:   string;        // ex: "Acct1.1.1" + label EN
  status:   'pass' | 'fail' | 'warning' | 'na';
  message:  string;
  date?:    string;
}

function buildRows(framework: CisFramework, ctrls: AnyControl[], scanByControl: Map<string, CisScanHit>): CisRow[] {
  const rows: CisRow[] = [];
  for (const c of ctrls) {
    const cisRefs = (c.references ?? []).filter((r: ControlReference) => r.framework === framework);
    if (cisRefs.length === 0) continue;
    const hit = scanByControl.get(c.id.toLowerCase());
    const refStr = cisRefs.map((r: ControlReference) => `${r.ref}${r.label ? ' — ' + r.label : ''}`).join(' · ');
    rows.push({
      id:      c.id,
      title:   c.title,
      cisRef:  refStr,
      status:  statusKey(hit?.status),
      message: hit?.message ?? '',
      date:    hit?.date,
    });
  }
  return rows;
}

function computeStats(rows: CisRow[]) {
  const total = rows.length;
  const conformes    = rows.filter((r) => r.status === 'pass').length;
  const nonConformes = rows.filter((r) => r.status === 'fail').length;
  const ameliorer    = rows.filter((r) => r.status === 'warning').length;
  const nonEvalues   = rows.filter((r) => r.status === 'na').length;
  const score = total > 0 ? Math.round(100 * conformes / total) : 0;
  return { total, conformes, nonConformes, ameliorer, nonEvalues, score };
}

// Dessine le mini-bouclier NIS365 (style favicon)
function drawShieldLogoPdf(doc: jsPDF, x: number, y: number, size: number): void {
  doc.setFillColor(...ACCENT);
  const topH = size * 0.65;
  doc.roundedRect(x, y, size, topH + 2, 1.5, 1.5, 'F');
  doc.lines(
    [ [size / 2, size - topH + 1], [-size / 2, -(size - topH + 1)] ],
    x, y + topH - 1, [1, 1], 'F', true
  );
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(size * 0.13);
  doc.line(x + size * 0.22, y + size * 0.48, x + size * 0.42, y + size * 0.65);
  doc.line(x + size * 0.42, y + size * 0.65, x + size * 0.78, y + size * 0.28);
}

// =============================================================================
// PDF
// =============================================================================

export function downloadCisReportPdf(
  framework: CisFramework,
  ctrls: AnyControl[],
  scanByControl: Map<string, CisScanHit>,
  meta: CisReportMeta,
): void {
  const lang = meta.lang ?? 'fr-BE';
  const i = STRINGS[lang];
  const rows = buildRows(framework, ctrls, scanByControl);
  const st = computeStats(rows);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  // ---- Page de garde ----
  doc.setFillColor(...BRAND); doc.rect(0, 0, W, 30, 'F');
  drawShieldLogoPdf(doc, 16, 6, 18);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(17); doc.text('NIS365', 39, 18);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.text('A U D I T O R', 39, 24);

  doc.setTextColor(...BRAND);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(28);
  doc.text(i.reportTitle(framework), W / 2, H / 2 - 25, { align: 'center' });

  doc.setFont('helvetica', 'normal'); doc.setFontSize(13); doc.setTextColor(70, 70, 70);
  doc.text(companyName(meta), W / 2, H / 2 - 8, { align: 'center' });
  doc.setFontSize(10); doc.setTextColor(110, 110, 110);
  doc.text(`${i.generatedOn} ${meta.date || today()}`, W / 2, H / 2 + 2, { align: 'center' });

  doc.setFillColor(...BRAND); doc.rect(0, H - 14, W, 14, 'F');
  doc.setTextColor(255, 255, 255); doc.setFontSize(8);
  doc.text(i.confidential, W / 2, H - 5, { align: 'center' });

  // ---- 1. Synthèse ----
  doc.addPage();
  doc.setTextColor(...BRAND); doc.setFont('helvetica', 'bold'); doc.setFontSize(18);
  doc.text(i.syntheseTitle, 14, 20);

  autoTable(doc, {
    startY: 28,
    body: [
      [i.total,        String(st.total)],
      [i.conformes,    String(st.conformes)],
      [i.ameliorer,    String(st.ameliorer)],
      [i.nonConformes, String(st.nonConformes)],
      [i.nonEvalues,   String(st.nonEvalues)],
      [i.score,        `${st.score} %`],
    ],
    theme: 'plain',
    styles: { fontSize: 11, cellPadding: 3, valign: 'top' },
    columnStyles: { 0: { cellWidth: 90, fontStyle: 'bold', textColor: BRAND }, 1: { cellWidth: 60, halign: 'right' } },
  });

  // ---- 2. Tableau détail ----
  doc.addPage();
  doc.setTextColor(...BRAND); doc.setFont('helvetica', 'bold'); doc.setFontSize(16);
  doc.text(i.tableTitle(framework), 14, 20);

  autoTable(doc, {
    startY: 26,
    head: [[i.colId, i.colTitle, i.colRef, i.colStatus, i.colEvidence]],
    body: rows.map((r) => [
      r.id,
      r.title,
      r.cisRef,
      statusLabel(r.status, i),
      r.message ? (r.message.length > 220 ? r.message.slice(0, 220) + '…' : r.message) : i.noEvidence,
    ]),
    styles: { fontSize: 7.5, cellPadding: 2, valign: 'top' },
    headStyles: { fillColor: BRAND, fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 22, fontStyle: 'bold', textColor: BRAND },
      1: { cellWidth: 50 },
      2: { cellWidth: 36, fontSize: 6.5 },
      3: { cellWidth: 22 },
      4: { cellWidth: 'auto', fontSize: 6.5, textColor: [80, 80, 80] as [number, number, number] },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 3) {
        const v = String(data.cell.raw);
        if (v === i.statusPass) data.cell.styles.textColor = GREEN;
        else if (v === i.statusFail) data.cell.styles.textColor = RED;
        else if (v === i.statusWarning) data.cell.styles.textColor = ORANGE;
      }
    },
  });

  doc.save(`${framework}_${safeName(companyName(meta))}_${today()}.pdf`);
}

// =============================================================================
// WORD (docx)
// =============================================================================

export async function downloadCisReportDocx(
  framework: CisFramework,
  ctrls: AnyControl[],
  scanByControl: Map<string, CisScanHit>,
  meta: CisReportMeta,
): Promise<void> {
  const lang = meta.lang ?? 'fr-BE';
  const i = STRINGS[lang];
  const rows = buildRows(framework, ctrls, scanByControl);
  const st = computeStats(rows);

  const cover: Paragraph[] = [
    new Paragraph({ children: [
      new TextRun({ text: '🛡 ', size: 36, color: '2E8B96' }),
      new TextRun({ text: 'NIS365 AUDITOR', bold: true, size: 28, color: '1F3D5C' }),
    ] }),
    new Paragraph({ children: [] }),
    new Paragraph({ children: [] }),
    new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: i.reportTitle(framework), bold: true, size: 56, color: '1F3D5C' })] }),
    new Paragraph({ children: [] }),
    new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: companyName(meta), bold: true, size: 28 })] }),
    new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: `${i.generatedOn} ${meta.date || today()}`, size: 22, color: '666666' })] }),
  ];

  const synthHeading = new Paragraph({ spacing: { before: 400, after: 200 },
    children: [new TextRun({ text: i.syntheseTitle, bold: true, size: 32, color: '1F3D5C' })] });

  const synthTable = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      [i.total,        String(st.total)],
      [i.conformes,    String(st.conformes)],
      [i.ameliorer,    String(st.ameliorer)],
      [i.nonConformes, String(st.nonConformes)],
      [i.nonEvalues,   String(st.nonEvalues)],
      [i.score,        `${st.score} %`],
    ].map(([k, v]) => new TableRow({ children: [
      new TableCell({ width: { size: 50, type: WidthType.PERCENTAGE }, children: [new Paragraph({ children: [new TextRun({ text: k, bold: true, color: '1F3D5C' })] })] }),
      new TableCell({ width: { size: 50, type: WidthType.PERCENTAGE }, children: [new Paragraph({ children: [new TextRun({ text: v })] })] }),
    ] })),
  });

  const tableHeading = new Paragraph({ spacing: { before: 400, after: 200 },
    children: [new TextRun({ text: i.tableTitle(framework), bold: true, size: 32, color: '1F3D5C' })] });

  const tableHead = new TableRow({ tableHeader: true, children: [i.colId, i.colTitle, i.colRef, i.colStatus, i.colEvidence].map((h) =>
    new TableCell({ shading: { fill: '1F3D5C' }, children: [new Paragraph({ children: [new TextRun({ text: h, bold: true, color: 'FFFFFF', size: 18 })] })] })
  )});
  const dataRows = rows.map((r) => new TableRow({ children: [
    new TableCell({ width: { size: 10, type: WidthType.PERCENTAGE }, children: [new Paragraph({ children: [new TextRun({ text: r.id, bold: true, color: '1F3D5C', size: 16 })] })] }),
    new TableCell({ width: { size: 25, type: WidthType.PERCENTAGE }, children: [new Paragraph({ children: [new TextRun({ text: r.title, size: 16 })] })] }),
    new TableCell({ width: { size: 20, type: WidthType.PERCENTAGE }, children: [new Paragraph({ children: [new TextRun({ text: r.cisRef, size: 14, color: '666666' })] })] }),
    new TableCell({ width: { size: 12, type: WidthType.PERCENTAGE }, children: [new Paragraph({ children: [new TextRun({
      text: statusLabel(r.status, i), size: 16, bold: true,
      color: r.status === 'pass' ? '1F7A45' : r.status === 'fail' ? 'B82329' : r.status === 'warning' ? 'A86317' : '787878',
    })] })] }),
    new TableCell({ width: { size: 33, type: WidthType.PERCENTAGE }, children: [new Paragraph({ children: [new TextRun({
      text: r.message ? (r.message.length > 200 ? r.message.slice(0, 200) + '…' : r.message) : i.noEvidence,
      size: 14, color: '555555',
    })] })] }),
  ] }));
  const detailTable = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [tableHead, ...dataRows],
  });

  const doc = new Document({
    creator: 'NIS365 Auditor',
    title: `${i.reportTitle(framework)} - ${companyName(meta)}`,
    sections: [{ children: [...cover, synthHeading, synthTable, tableHeading, detailTable] }],
  });
  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${framework}_${safeName(companyName(meta))}_${today()}.docx`);
}
